import React from "react"
import { DeepReadonly } from "ts-essentials"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { DEFAULT_CORE_STATE } from "../store/core-slice"
import AnalyticsContext, { useAnalyticsWithProperties } from "connected-components/analytics/context"
import { useExtractDashboardContextProperties } from "./extract-context-properties"

interface DashboardAnalyticsProviderProps {
  children: React.ReactNode
}

/**
 * Provides dashboard analytics context with automatic property extraction
 * and "Dashboard" prefix for all events.
 *
 * This provider wraps children with analytics context that includes:
 * - Dashboard-specific context properties (modelId, instrumentId, etc.)
 * - "Dashboard" prefix for all tracked events
 * - Inheritance from parent analytics contexts if nested
 *
 */
export function DashboardAnalyticsProvider({
  children,
}: DeepReadonly<DashboardAnalyticsProviderProps>) {
  const dashboardId = useDashboardId()
  const coreState = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)

  // hook handles memoization internally — analyticsFields does not need useMemo
  const analyticsFields = {
    modelId,
    selectedMode: coreState.selectedMode,
    period: coreState.period,
    isIntraday: coreState.isIntraday,
    isEndVsStartDateComparison: coreState.isEndVsStartDateComparison,
    instrumentsMetadata: coreState.instrumentsMetadata,
    instrument: coreState.instrument,
    comparisonInstrument: coreState.comparisonInstrument,
    base: coreState.base,
    secondInstrument: coreState.secondInstrument,
  }
  const contextProperties = useExtractDashboardContextProperties(analyticsFields)

  const analyticsContextValue = useAnalyticsWithProperties(contextProperties, "Dashboard")

  return (
    <AnalyticsContext.Provider value={analyticsContextValue}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export default DashboardAnalyticsProvider
